import { atom } from 'jotai'

import { api } from '../lib/api'

export type PhotoStatus = 'pending' | 'processing' | 'approved' | 'rejected'

export type Face = {
  boundingBox: [number, number, number, number] // [x, y, width, height]
  confidence: number
}

export type Photo = {
  id: string
  profileId: string
  userId: string
  status: PhotoStatus
  faces?: Face[]
  imageWidth?: number
  imageHeight?: number
  createdAt: string
  updatedAt?: string
}

export const photosAtom = atom<Photo[]>([])
export const photosIsLoadingAtom = atom(false)
export const photosErrorAtom = atom<string | null>(null)

export const fetchPhotosAtom = atom(null, async function fetchPhotos(_get, set, profileId: string) {
  set(photosIsLoadingAtom, true)
  set(photosErrorAtom, null)

  try {
    const response = await api.fetch(`/profile/${profileId}/photos`)

    if (!response.ok) {
      set(photosErrorAtom, 'Failed to fetch photos')
      set(photosIsLoadingAtom, false)
      return []
    }

    const photos = (await response.json()) as Photo[]
    set(photosAtom, photos)
    set(photosIsLoadingAtom, false)
    return photos
  } catch (_err) {
    set(photosErrorAtom, 'Network error')
    set(photosIsLoadingAtom, false)
    return []
  }
})

/** Web File/Blob or React Native FormData file-like object */
export type PhotoUploadSource = File | Blob | { uri: string; name: string; type: string }

export const uploadPhotoAtom = atom(
  null,
  async function uploadPhoto(_get, set, profileId: string, file: PhotoUploadSource) {
    set(photosIsLoadingAtom, true)
    set(photosErrorAtom, null)

    try {
      const formData = new FormData()
      // RN FormData accepts { uri, name, type }; browsers accept File/Blob.
      formData.append('photo', file as unknown as Blob)

      const response = await api.fetch(`/profile/${profileId}/photos`, {
        method: 'POST',
        body: formData,
      })

      if (!response.ok) {
        set(photosErrorAtom, 'Failed to upload photo')
        set(photosIsLoadingAtom, false)
        return null
      }

      const photo = (await response.json()) as Photo
      set(photosAtom, (prev) => [...prev, photo])
      set(photosIsLoadingAtom, false)
      return photo
    } catch (_err) {
      set(photosErrorAtom, 'Network error')
      set(photosIsLoadingAtom, false)
      return null
    }
  },
)

export const deletePhotoAtom = atom(null, async function deletePhoto(_get, set, photoId: string) {
  set(photosIsLoadingAtom, true)
  set(photosErrorAtom, null)

  try {
    const response = await api.fetch(`/photos/${photoId}`, {
      method: 'DELETE',
    })

    if (!response.ok) {
      set(photosErrorAtom, 'Failed to delete photo')
      set(photosIsLoadingAtom, false)
      return false
    }

    set(photosAtom, (prev) => prev.filter((p) => p.id !== photoId))
    set(photosIsLoadingAtom, false)
    return true
  } catch (_err) {
    set(photosErrorAtom, 'Network error')
    set(photosIsLoadingAtom, false)
    return false
  }
})

export const clearPhotosAtom = atom(null, function clearPhotos(_get, set) {
  set(photosAtom, [])
  set(photosErrorAtom, null)
})
